/*
 * A small preview made on the sender's device, before encrypting: a video's cover frame (and its length and
 * shape), or a tiny blurred stand-in for a photo. It travels inside the encrypted message, so the recipient
 * sees what is coming at once (like WhatsApp) and the server never sees any of it.
 */

export interface MediaPreview {
  /** data:image/jpeg;base64,… (a few kB). */
  thumb: string;
  width: number;
  height: number;
  durationMs?: number;
}

const POSTER_MAX = 360;
const PHOTO_MAX = 40;

function draw(source: CanvasImageSource, w: number, h: number, max: number, quality: number): string | null {
  if (!w || !h) return null;
  const k = Math.min(1, max / Math.max(w, h));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(w * k));
  canvas.height = Math.max(1, Math.round(h * k));
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  const url = canvas.toDataURL('image/jpeg', quality);
  return url.startsWith('data:image/jpeg') ? url : null;
}

/** A video's cover (a frame about a second in), its length and size. Null if the browser cannot read it. */
export function videoPreview(file: Blob, timeoutMs = 6000): Promise<MediaPreview | null> {
  return new Promise((resolve) => {
    const src = URL.createObjectURL(file);
    const video = document.createElement('video');
    let settled = false;
    const done = (value: MediaPreview | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      video.removeAttribute('src');
      video.load();
      URL.revokeObjectURL(src);
      resolve(value);
    };
    const timer = setTimeout(() => done(null), timeoutMs);
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.onerror = () => done(null);
    video.onloadedmetadata = () => {
      const d = Number.isFinite(video.duration) ? video.duration : 0;
      video.currentTime = Math.min(1, d / 10 || 0);
    };
    video.onseeked = () => {
      const thumb = draw(video, video.videoWidth, video.videoHeight, POSTER_MAX, 0.62);
      done(thumb ? { thumb, width: video.videoWidth, height: video.videoHeight, ...(Number.isFinite(video.duration) && video.duration > 0 ? { durationMs: Math.round(video.duration * 1000) } : {}) } : null);
    };
    video.src = src;
  });
}

/** A photo's shape and a tiny stand-in (shown blurred while the real one decrypts, so nothing jumps). */
export async function photoPreview(file: Blob): Promise<MediaPreview | null> {
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const thumb = draw(bitmap, bitmap.width, bitmap.height, PHOTO_MAX, 0.5);
    const out = thumb ? { thumb, width: bitmap.width, height: bitmap.height } : null;
    bitmap.close();
    return out;
  } catch {
    return null;
  }
}

/** Only small JPEG/PNG/WebP data URLs from a message are ever shown (nothing else from the other side). */
export function safeThumb(value: unknown): string | null {
  return typeof value === 'string' && value.length < 120_000 && /^data:image\/(jpeg|png|webp);base64,[A-Za-z0-9+/=]+$/.test(value) ? value : null;
}
